import { Fragment, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader, CardBody } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState } from '../../components/ui/EmptyState'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { Toggle } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import type { PlanoRecursoValor, RecursoPlanoAdmin } from '../../types'

const TIPO_LIMITE = 2

const chaveCelula = (planoId: string, chave: string) => `${planoId}|${chave}`

/** Matriz recurso x plano: o que cada plano libera, valendo na hora para todos os assinantes. */
export function RecursosPlanoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  // Só as células alteradas ficam aqui; o resto vem do servidor.
  const [edicoes, setEdicoes] = useState<Record<string, PlanoRecursoValor>>({})

  const { data, isLoading } = useQuery({ queryKey: ['admin-recursos-plano'], queryFn: adminApi.recursosPlano })

  const originais = useMemo(() => {
    const m = new Map<string, PlanoRecursoValor>()
    data?.valores.forEach((v) => m.set(chaveCelula(v.planoId, v.chave), v))
    return m
  }, [data])

  const grupos = useMemo(() => {
    const porModulo = new Map<string, RecursoPlanoAdmin[]>()
    ;[...(data?.recursos ?? [])].sort((a, b) => a.ordem - b.ordem).forEach((r) => {
      porModulo.set(r.modulo, [...(porModulo.get(r.modulo) ?? []), r])
    })
    return [...porModulo.entries()]
  }, [data])

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarRecursosPlano(Object.values(edicoes)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-recursos-plano'] })
      qc.invalidateQueries({ queryKey: ['assinatura'] })
      qc.invalidateQueries({ queryKey: ['planos'] })
      setEdicoes({})
      toast.success('Recursos salvos.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function valorDe(planoId: string, chave: string): PlanoRecursoValor {
    const k = chaveCelula(planoId, chave)
    return edicoes[k] ?? originais.get(k) ?? { planoId, chave, habilitado: false, limite: null }
  }

  function alterar(planoId: string, chave: string, parcial: Partial<PlanoRecursoValor>) {
    const k = chaveCelula(planoId, chave)
    const novo = { ...valorDe(planoId, chave), ...parcial }
    const original = originais.get(k)
    const igual = original ? original.habilitado === novo.habilitado && original.limite === novo.limite : !novo.habilitado && novo.limite === null
    setEdicoes((e) => {
      const resto = { ...e }
      delete resto[k]
      return igual ? resto : { ...resto, [k]: novo }
    })
  }

  const pendentes = Object.keys(edicoes).length
  const planos = data?.planos ?? []

  return (
    <>
      <PageHeader title="Recursos por plano" />
      <Card>
        <CardHeader title="Recursos por plano" icon="ri-toggle-line" />
        <CardBody>
          <div className="space-y-4">
            <p className="text-13 text-muted">
              Define o que cada plano libera. A mudança vale na hora para todos os assinantes do plano. Em limites, campo vazio significa ilimitado e 0 significa que não permite.
              Os recursos antigos (contas, cartões, notas fiscais etc.) continuam na tela de Planos.
            </p>

            {isLoading ? <LoadingState /> : (
              <Table>
                <THead>
                  <tr>
                    <Th>Recurso</Th>
                    {planos.map((p) => <Th key={p.id} className="text-center">{p.nome}</Th>)}
                  </tr>
                </THead>
                <tbody>
                  {grupos.map(([modulo, recursos]) => (
                    <Fragment key={modulo}>
                      <tr className="bg-surface2/60">
                        <td colSpan={planos.length + 1} className="px-4 py-2 text-xs font-semibold uppercase text-muted">{modulo}</td>
                      </tr>
                      {recursos.map((r) => (
                        <Tr key={r.chave}>
                          <Td>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-white">{r.nome}</span>
                              {r.fase === 2 && (
                                <span title="Fase 2: só funciona quando for entregue">
                                  <Badge variant="warning">Fase 2</Badge>
                                </span>
                              )}
                            </div>
                            {r.descricao && <p className="text-xs text-muted">{r.descricao}</p>}
                            <p className="font-mono text-2xs text-muted/70">{r.chave}</p>
                          </Td>
                          {planos.map((p) => {
                            const v = valorDe(p.id, r.chave)
                            const alterado = !!edicoes[chaveCelula(p.id, r.chave)]
                            return (
                              <Td key={p.id} className={alterado ? 'bg-primary/5 text-center' : 'text-center'}>
                                {r.tipo === TIPO_LIMITE ? (
                                  <input
                                    type="number"
                                    min={0}
                                    inputMode="numeric"
                                    className="form-control mx-auto w-24 text-center"
                                    placeholder="Ilimitado"
                                    aria-label={`${r.nome} no plano ${p.nome}`}
                                    value={v.limite ?? ''}
                                    onChange={(e) => {
                                      const n = e.target.value === '' ? null : Math.max(0, Math.floor(Number(e.target.value)))
                                      alterar(p.id, r.chave, { limite: n, habilitado: true })
                                    }}
                                  />
                                ) : (
                                  <Toggle label="" checked={v.habilitado} onChange={(c) => alterar(p.id, r.chave, { habilitado: c })} />
                                )}
                              </Td>
                            )
                          })}
                        </Tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </Table>
            )}

            <div className="flex items-center justify-end gap-3">
              <span className="text-13 text-muted">
                {pendentes === 0 ? 'Nenhuma alteração' : `${pendentes} ${pendentes === 1 ? 'alteração não salva' : 'alterações não salvas'}`}
              </span>
              {pendentes > 0 && <Button variant="light" onClick={() => setEdicoes({})} disabled={salvar.isPending}>Descartar</Button>}
              <Button icon="ri-save-line" onClick={() => salvar.mutate()} loading={salvar.isPending} disabled={pendentes === 0}>Salvar alterações</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
